import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Mic, Loader2, AlertTriangle, MapPin, Calendar, Heart, Stethoscope } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { API } from "../App";
import VoiceModeModal from "../components/VoiceModeModal";
import { callGeminiAI } from "../lib/gemini";

const GEMINI_SYSTEM_PROMPT = `You are SupaDoc, an expert AI medical assistant AND health educator. You help with:

1. CLINICAL: Symptoms, diseases, diagnosis, treatment, medications, first aid
2. EDUCATION: Medical concepts (anatomy, physiology, pathology, pharmacology, biochemistry, microbiology, surgery, medicine)
3. EXAM PREP: NEET UG/PG, USMLE, MBBS, nursing, paramedic — explain topics in depth with examples, mnemonics, and step-by-step reasoning
4. WELLNESS: Nutrition, fitness, mental health, preventive care
5. RESEARCH: Explain clinical studies, drug mechanisms, disease pathophysiology in detail

Formatting rules (always follow):
- Use ## for main section headings
- Use ### for sub-headings
- Use **bold** for key terms, drug names, diagnoses
- Use numbered lists for steps/mechanisms
- Use bullet points for features/symptoms lists
- Use > for important warnings or notes
- Keep explanations clear, structured, and detailed
- For education topics, teach step-by-step like a professor
- Add mnemonics where helpful

Language: Respond in the same language the user writes in (Hindi, Hinglish, or English).
Always recommend consulting a real doctor for serious/emergency conditions.`;

// ── Markdown renderer ─────────────────────────────────────────────
const renderInline = (line) => {
  const parts = line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**'))
      return <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`'))
      return <code key={i} className="bg-blue-50 text-primary-600 text-xs font-mono px-1.5 py-0.5 rounded">{part.slice(1, -1)}</code>;
    return part;
  });
};

const FormattedMessage = ({ text }) => {
  const lines = text.split('\n');
  return (
    <div className="space-y-1.5 text-sm leading-relaxed">
      {lines.map((line, i) => {
        const t = line.trim();
        if (!t) return <div key={i} className="h-1" />;
        if (t.startsWith('## '))
          return <h2 key={i} className="text-base font-bold text-slate-900 mt-3 mb-1 pb-1 border-b border-blue-100">{t.slice(3)}</h2>;
        if (t.startsWith('### '))
          return <h3 key={i} className="text-sm font-bold text-primary-700 mt-2 mb-0.5">{t.slice(4)}</h3>;
        if (t.startsWith('#### '))
          return <h4 key={i} className="text-xs font-bold text-primary-500 mt-1.5 mb-0.5 uppercase tracking-wide">{t.slice(5)}</h4>;
        if (t === '---' || t === '***')
          return <hr key={i} className="border-slate-200 my-2" />;
        if (t.startsWith('- ') || t.startsWith('* '))
          return (
            <div key={i} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0" />
              <span>{renderInline(t.slice(2))}</span>
            </div>
          );
        if (/^\d+\.\s/.test(t)) {
          const num = t.match(/^(\d+)\.\s/)?.[1];
          const content = t.replace(/^\d+\.\s/, '');
          return (
            <div key={i} className="flex items-start gap-2">
              <span className="font-bold text-primary-500 flex-shrink-0 min-w-[18px]">{num}.</span>
              <span>{renderInline(content)}</span>
            </div>
          );
        }
        if (t.startsWith('> '))
          return (
            <div key={i} className="border-l-4 border-amber-400 pl-3 py-1 bg-amber-50 rounded-r text-amber-800 text-xs">
              {renderInline(t.slice(2))}
            </div>
          );
        return <p key={i}>{renderInline(t)}</p>;
      })}
    </div>
  );
};

// ── Typing animation ──────────────────────────────────────────────────
const TypingText = ({ text, isNew }) => {
  const [displayed, setDisplayed] = useState(isNew ? "" : text);
  const [done, setDone] = useState(!isNew);

  useEffect(() => {
    if (!isNew) { setDisplayed(text); setDone(true); return; }
    setDisplayed("");
    setDone(false);
    let i = 0;
    const interval = setInterval(() => {
      setDisplayed(text.slice(0, i + 1));
      i++;
      if (i >= text.length) { clearInterval(interval); setDone(true); }
    }, 10);
    return () => clearInterval(interval);
  }, [text, isNew]);

  if (done) return <FormattedMessage text={text} />;
  return <p className="whitespace-pre-wrap text-sm leading-relaxed">{displayed}</p>;
};

const Chat = ({ user }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showVoiceMode, setShowVoiceMode] = useState(false);
  const [latestMsgId, setLatestMsgId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Initial welcome message
    setMessages([
      {
        id: "welcome",
        role: "assistant",
        content: "Namaste! I'm SupaDoc AI Doctor. How can I help you today?\n\nYou can describe your symptoms in Hindi, Hinglish, or English. I'm here 24/7 to assist you.",
        triage: null,
      },
    ]);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: messageText,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const { error, output } = await callGeminiAI(
        [
          ...messages.filter((msg) => msg.id !== "welcome").map((msg) => ({
            role: msg.role,
            content: msg.content,
          })),
          {
            role: "user",
            content: messageText,
          },
        ],
        GEMINI_SYSTEM_PROMPT,
      );

      if (error) {
        console.error("Gemini API error:", error);
        toast.error("Failed to get AI response. Please try again.");
        setLoading(false);
        return;
      }

      const responseText = output || "Sorry, I could not generate a response right now.";

      const newId = (Date.now() + 1).toString();
      const assistantMessage = {
        id: newId,
        role: "assistant",
        content: responseText,
        triage: null,
      };

      setLatestMsgId(newId);
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceExchange = (userText, aiText) => {
    const uid = Date.now().toString();
    const aid = (Date.now() + 1).toString();
    setLatestMsgId(aid);
    setMessages(prev => [
      ...prev,
      { id: uid, role: "user", content: userText },
      { id: aid, role: "assistant", content: aiText, triage: null },
    ]);
  };

  const getSeverityStyles = (severity) => {
    switch (severity?.toUpperCase()) {
      case "CRITICAL":
        return "bg-red-50 border-red-200 text-red-800";
      case "MODERATE":
        return "bg-amber-50 border-amber-200 text-amber-800";
      default:
        return "bg-green-50 border-green-200 text-green-800";
    }
  };

  const getSeverityBadgeStyles = (severity) => {
    switch (severity?.toUpperCase()) {
      case "CRITICAL":
        return "severity-critical";
      case "MODERATE":
        return "severity-moderate";
      default:
        return "severity-mild";
    }
  };

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-blue-50 via-white to-blue-100">
      {/* Header */}
      <div className="glass-panel sticky top-0 z-40 px-4 py-4 border-b border-white/30">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-semibold text-slate-800">AI Doctor</h1>
            <p className="text-xs text-green-600 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Online - Ready to help
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6 chat-scroll" data-testid="chat-messages">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div className={`max-w-[85%] ${message.role === "user" ? "order-2" : "order-1"}`}>
                {message.role === "assistant" && (
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                      <Heart className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs text-slate-500">SupaDoc AI</span>
                  </div>
                )}

                <div
                  className={message.role === "user" ? "chat-bubble-user p-4" : "chat-bubble-ai p-4"}
                  data-testid={`chat-message-${message.role}`}
                >
                  {message.role === "assistant" ? (
                    <TypingText text={message.content} isNew={message.id === latestMsgId} />
                  ) : (
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                  )}
                </div>

                {/* Triage Result */}
                {message.triage && message.triage.severity && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className={`mt-3 rounded-2xl border p-4 triage-card ${getSeverityStyles(message.triage.severity)}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getSeverityBadgeStyles(message.triage.severity)}`}>
                        {message.triage.severity}
                      </span>
                      {message.triage.department && (
                        <span className="text-xs text-slate-600">{message.triage.department}</span>
                      )}
                    </div>

                    {message.triage.advice && (
                      <p className="text-sm mb-3">{message.triage.advice}</p>
                    )}

                    {message.triage.next_step && (
                      <p className="text-sm font-medium mb-3">{message.triage.next_step}</p>
                    )}

                    {message.triage.severity === "CRITICAL" && (
                      <div className="bg-red-100 border border-red-300 rounded-xl p-3 mb-3">
                        <div className="flex items-center gap-2 text-red-700">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-sm font-semibold">Emergency Alert</span>
                        </div>
                        <p className="text-xs text-red-600 mt-1">
                          {message.triage.emergency || "Please seek immediate medical attention or tap Emergency below."}
                        </p>
                      </div>
                    )}

                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 rounded-full text-xs"
                        onClick={() => navigate("/nearby")}
                        data-testid="find-nearby-btn"
                      >
                        <MapPin className="w-3 h-3 mr-1" />
                        Find {message.triage.nearest_care || "Care"}
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 rounded-full text-xs bg-primary-500 hover:bg-primary-600"
                        onClick={() => navigate("/bookings")}
                        data-testid="book-appointment-btn"
                      >
                        <Calendar className="w-3 h-3 mr-1" />
                        Book Now
                      </Button>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="chat-bubble-ai p-4">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
                <span className="text-sm text-slate-500">Analyzing your symptoms...</span>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Bar */}
      <div className="fixed bottom-20 left-0 right-0 px-4 pb-4">
        <div className="max-w-2xl mx-auto">
          <div className="glass-panel rounded-full p-2 flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full shrink-0 hover:bg-blue-50"
              onClick={() => setShowVoiceMode(true)}
              data-testid="voice-input-btn"
            >
              <Mic className="w-5 h-5 text-slate-500" />
            </Button>
            
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Describe your symptoms..."
              className="flex-1 bg-transparent border-0 outline-none text-slate-800 placeholder:text-slate-400 text-sm py-2"
              disabled={loading}
              data-testid="chat-input"
            />
            
            <Button
              size="icon"
              className="rounded-full shrink-0 bg-primary-500 hover:bg-primary-600"
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              data-testid="send-message-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="fixed bottom-36 left-0 right-0 px-4 overflow-x-auto">
        <div className="max-w-2xl mx-auto flex gap-2 pb-2">
          {["Headache", "Fever", "Cough", "Stomach pain", "Skin rash"].map((symptom) => (
            <Button
              key={symptom}
              variant="outline"
              size="sm"
              className="rounded-full whitespace-nowrap bg-white/80 border-slate-200 text-xs"
              onClick={() => sendMessage(`I have ${symptom.toLowerCase()}`)}
              disabled={loading}
              data-testid={`quick-symptom-${symptom.toLowerCase().replace(" ", "-")}`}
            >
              {symptom}
            </Button>
          ))}
        </div>
      </div>

      {/* Voice Mode Modal */}
      <VoiceModeModal
        open={showVoiceMode}
        onClose={() => setShowVoiceMode(false)}
        onExchange={handleVoiceExchange}
      />
    </div>
  );
};

export default Chat;
